/**
 * 予約
 * 予約フォームの入力値に対するバリデーションルールを定義します。
 * 開始時間・終了時間の整合性、必須項目のチェック、顧客ステータスに応じた
 * バリデーションを行います。
 *
 * @file rule.ts
 * @author LineCRM Team
 * @version 1.0.0
 */

import { BookingCustomerStatus } from "@/clinic/typings/enums/booking";
import { MaybeRefOrGetter } from "@vueuse/core";
import type { Rule, Rules } from "async-validator";

/**
 * フォームバリデーション用の型定義
 * @interface IFormRuleValidation
 */
type IFormRuleValidation = {
  /** 開始時間 */
  startTime: string;
  /** 終了時間 */
  endTime: string;
  /** 顧客ステータス */
  customerStatus: string;
  /** 自由予約フラグ（オプション） */
  is_free_space?: boolean;
};
/** 必須項目のエラーメッセージ */
const emptyMsg = "*このフィールドは必須です。";

/**
 * 新規顧客用のバリデーションルール
 * @constant {Record<string, Rule | Rules>} newCustomerFieldValidates
 */
const newCustomerFieldValidates: Record<string, Rule | Rules> = {
  customerName: {
    type: "string",
    required: true,
    message: emptyMsg,
  },
};

/**
 * 既存顧客用のバリデーションルール
 * @constant {Record<string, Rule | Rules>} existedFieldValidates
 */
const existedFieldValidates: Record<string, Rule | Rules> = {
  customerId: {
    type: "number",
    required: true,
    message: emptyMsg,
    validator: (_rule, customerId: number) => customerId > 0,
  },
};

/**
 * 顧客ステータスに応じたバリデーションルールを取得する
 * @param {string} customerStatus - 顧客ステータス
 * @returns {Record<string, Rule | Rules>} バリデーションルール
 */
const newFieldValidates = (customerStatus: string): Record<string, Rule | Rules> =>
  customerStatus == BookingCustomerStatus.new ? newCustomerFieldValidates : existedFieldValidates;

/**
 * 予約フォームのバリデーションルールを生成する
 * @param {IFormRuleValidation} fieldTime - バリデーション対象のフィールド情報
 * @returns {MaybeRefOrGetter<Record<string, Rule | Rules>>} バリデーションルール
 */
const rules = (fieldTime: IFormRuleValidation): MaybeRefOrGetter<Record<string, Rule | Rules>> => {
  const { startTime: start, endTime: end, customerStatus, is_free_space } = fieldTime;

  /**
   * 開始時間と終了時間の整合性をチェックする
   * @returns {boolean} バリデーション結果（true: 正常, false: エラー）
   */
  const hasValidateHour = (): boolean => {
    if (!start || !end) return true; // どちらかが空の場合はバリデーションをスキップ

    try {
      // 日付オブジェクトに変換して比較
      const startDate = new Date(start);
      const endDate = new Date(end);

      // 無効な日付の場合はバリデーションをスキップ
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return true;
      }

      return endDate > startDate;
    } catch (e) {
      // エラーの場合はバリデーションをスキップ
      return true;
    }
  };

  /**
   * 値が空白でないかチェックする
   * @param {any} value - チェック対象の値
   * @returns {boolean} バリデーション結果
   */
  const notBlank = (value: any): boolean => !!value && parseInt(value) > 0;

  /** バリデーションルールの定義 */
  const validationRules: Record<string, Rule | Rules> = {
    startTime: [
      {
        type: "string",
        required: true,
        message: emptyMsg,
      },
      {
        validator: () => hasValidateHour(),
        message: "終了時間より大きくすることはできません。",
      },
    ],
    endTime: [
      {
        type: "string",
        required: true,
        message: emptyMsg,
      },
      {
        validator: () => hasValidateHour(),
        message: "開始時間より過去にすることはできません。",
      },
    ],
    medicalMenuId: {
      type: "number",
      required: true,
      message: emptyMsg,
      min: 1,
    },
    staffId: {
      validator: (_rule, value: any) => notBlank(value),
      message: emptyMsg,
    },
    ...newFieldValidates(customerStatus),
  };

  // 自由予約でない場合のみ部屋IDのバリデーションを追加
  if (!is_free_space) {
    validationRules.roomId = {
      validator: (_rule, value: any) => notBlank(value),
      message: emptyMsg,
    };
  }

  return validationRules;
};

/**
 * 予約フォームのバリデーションルールをエクスポート
 * @default rules
 */
export default rules;
